import React, { useState, useEffect, useRef } from 'react';
import { Container, TextField, Button, Typography, Grid, Paper, Accordion, AccordionSummary, AccordionDetails, Snackbar, Alert, Box, IconButton, Tooltip } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { fetchCompanies, saveCompany, deleteCompany } from '../api/CompanyApi'; // Import API methods

const CompanyDetails = () => {
  const [companies, setCompanies] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [gridApi, setGridApi] = useState(null); // Define gridApi state
  const editSectionRef = useRef(null);

  useEffect(() => {
    loadCompanies();
  }, []);

  const loadCompanies = async () => {
    try {
      const companiesData = await fetchCompanies();
      console.log('Company Details:', companiesData); // Log the data to verify
      setCompanies(companiesData);
      if (companiesData.length > 0) {
        setSelectedCompany(companiesData[0]);
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
      setSnackbar({ open: true, message: 'Failed to fetch companies', severity: 'error' });
    }
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    if (gridApi) {
      gridApi.setGridOption('quickFilterText', event.target.value);
    }
  };

  const handleRowClick = (event) => {
    const company = event.data;
    setSelectedCompany(company);
    setTimeout(() => {
      editSectionRef.current.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setSelectedCompany({ ...selectedCompany, [name]: value });
  };

  const handleSave = async () => {
    try {
      await saveCompany(selectedCompany);
      setSnackbar({ open: true, message: 'Company saved successfully', severity: 'success' });
      loadCompanies();
    } catch (error) {
      console.error('Error saving company:', error);
      setSnackbar({ open: true, message: 'Failed to save company', severity: 'error' });
    }
  };

  const handleNewCompany = () => {
    setSelectedCompany({
      companyId: null,
      companyName: '',
      address: '',
      phoneNo: '',
      emailId: '',
    });
  };

  const handleDeleteCompany = async () => {
    try {
      await deleteCompany(selectedCompany.companyId);
      setSnackbar({ open: true, message: 'Company deleted successfully', severity: 'success' });
      loadCompanies();
      setSelectedCompany(null);
    } catch (error) {
      console.error('Error deleting company:', error);
      setSnackbar({ open: true, message: 'Failed to delete company', severity: 'error' });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const columnDefs = [
    { headerName: 'Company Name', field: 'companyName', sortable: true, filter: true },
    { headerName: 'Address', field: 'address', sortable: true, filter: true },
    { headerName: 'Phone No', field: 'phoneNo', sortable: true, filter: true },
    { headerName: 'Email ID', field: 'emailId', sortable: true, filter: true },
  ];

  const defaultColDef = {
    flex: 1,
    minWidth: 150,
    filter: true,
    sortable: true,
    resizable: true,
  };

  const onGridReady = (params) => {
    setGridApi(params.api);
    if (params.api.getDisplayedRowCount() > 0) {
      const firstRow = params.api.getDisplayedRowAtIndex(0);
      params.api.selectNode(firstRow, true);
      setSelectedCompany(firstRow.data);
      setTimeout(() => {
        editSectionRef.current.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <TextField
          label="Search Companies"
          variant="outlined"
          value={searchTerm}
          onChange={handleSearchChange}
          fullWidth
          InputProps={{
            endAdornment: (
              <IconButton>
                <SearchIcon />
              </IconButton>
            ),
          }}
        />
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Paper elevation={3} sx={{ padding: 2 }}>
            <Box className="ag-theme-alpine" sx={{ height: 400, width: '100%' }}>
              <AgGridReact
                columnDefs={columnDefs}
                rowData={companies}
                pagination={true}
                paginationPageSize={10}
                onRowClicked={handleRowClick}
                onGridReady={onGridReady}
                defaultColDef={defaultColDef}
              />
            </Box>
          </Paper>
        </Grid>
        {selectedCompany && (
          <Grid item xs={12}>
            <Accordion expanded ref={editSectionRef} sx={{ marginTop: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">Edit Company</Typography>
                <Box ml={2}>
                  <Button variant="contained" color="primary" startIcon={<SaveIcon />} onClick={handleSave} sx={{ marginRight: 1 }}>
                    Save
                  </Button>
                  <Button variant="contained" color="secondary" startIcon={<DeleteIcon />} onClick={handleDeleteCompany} sx={{ marginRight: 1 }}>
                    Delete
                  </Button>
                  <Button variant="contained" color="default" startIcon={<AddIcon />} onClick={handleNewCompany}>
                    New
                  </Button>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Company Name"
                      variant="outlined"
                      fullWidth
                      name="companyName"
                      value={selectedCompany.companyName}
                      onChange={handleInputChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Address"
                      variant="outlined"
                      fullWidth
                      name="address"
                      value={selectedCompany.address}
                      onChange={handleInputChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Phone No"
                      variant="outlined"
                      fullWidth
                      name="phoneNo"
                      value={selectedCompany.phoneNo}
                      onChange={handleInputChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Email ID"
                      variant="outlined"
                      fullWidth
                      name="emailId"
                      value={selectedCompany.emailId}
                      onChange={handleInputChange}
                    />
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Grid>
        )}
      </Grid>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CompanyDetails;